import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import type { AvailabilitySettings } from "@/lib/availability";
import { useProfileData, extractWakeTimeFromProfile, type ProfileData } from "@/hooks/shared/use-profile-data";

// Global caches with longer duration to prevent excessive API calls
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes (increased from 10)
const STALE_TIME = 10 * 60 * 1000; // 10 minutes before considering data stale
const GC_TIME = 30 * 60 * 1000; // 30 minutes before garbage collection

// Enhanced settings cache with structured data and timestamps
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Profile data now comes from shared hook - remove duplicate interface

// Global caches for cross-component sharing
const settingsCache: Record<string, CacheEntry<AvailabilitySettings>> = {};
const profileCache: Record<string, CacheEntry<ProfileData>> = {};

// Request deduplication mechanism
const PENDING_REQUESTS: Record<string, Promise<any>> = {};

/**
 * Hook to fetch and cache host settings with intelligent deduplication
 * This is the central source of truth for host settings and profile data
 */
export function useHostSettings(
  hostUserId?: string,
  defaultSettings?: any,
  selectedDate?: Date
) {
  const cacheKey = hostUserId || 'self';
  const queryKey = ['host-settings', hostUserId, selectedDate?.toDateString()];

  // Use shared profile data hook to eliminate duplicate API calls
  const { data: profileData, isLoading: profileLoading, error: profileError } = useProfileData(hostUserId);

  // Use react-query with proper caching strategy
  const { data: hostSettings, isLoading: loading, error } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      console.log(`[useHostSettings] Fetching settings for ${cacheKey}`);
      
      // Check cache first to avoid unnecessary requests
      const cached = settingsCache[cacheKey];
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        console.log(`[useHostSettings] Using cached settings for ${cacheKey}`);
        return cached.data;
      }

      // For debug mode, use default settings
      if (hostUserId === 'debug') {
        console.log('[useHostSettings] Debug mode, using default settings');
        return defaultSettings || {
          bufferBefore: 15,
          bufferAfter: 15,
          minNoticeTime: 60
        };
      }

      // Check if we already have a pending request for this host to prevent duplicate calls
      if (PENDING_REQUESTS[`settings_${cacheKey}`]) {
        console.log(`[useHostSettings] Request already in progress for ${cacheKey}, reusing promise`);
        return PENDING_REQUESTS[`settings_${cacheKey}`];
      }

      try {
        // Create a new request promise for deduplication
        const requestPromise = new Promise(async (resolve, reject) => {
          try {
        // Skip DB call if no valid user ID - return defaults for preview/demo mode
        if (!hostUserId || hostUserId === 'debug' || hostUserId === 'undefined' || hostUserId === 'null') {
          console.log('[useHostSettings] No valid user ID, using default settings for preview mode');
          resolve({
            ...defaultSettings,
            timezone: 'America/Chicago',
            userEnergySettings: null
          });
          return;
        }

        const { data: settings, error: settingsError } = await supabase
          .from('availability_settings')
          .select('*')
          .eq('user_id', hostUserId)
          .maybeSingle();

            if (settingsError) throw settingsError;

            console.log('[useHostSettings] Raw settings from DB:', settings);

            // Profile data comes from the shared hook - no need to fetch again
            console.log('[useHostSettings] Profile data from shared hook:', profileData);

            // Extract timezone with priority: settings.timezone > profile.energy_profile.timezone
            let timezone = settings?.timezone;
            
            if (!timezone && profileData?.energy_profile && typeof profileData.energy_profile === 'object') {
              timezone = profileData.energy_profile.timezone;
            }

            console.log('[useHostSettings] Final timezone from DB:', timezone);

            // Fetch weekly availability for the selected date
            let weeklyAvailability = null;
            if (selectedDate && hostUserId && hostUserId !== 'debug') {
              const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
              const { data: weeklyData, error: weeklyError } = await supabase
                .from('weekly_availability')
                .select('*')
                .eq('user_id', hostUserId)
                .eq('day_of_week', dayOfWeek)
                .eq('is_available', true)
                .order('slot_sequence')
                .limit(1)
                .maybeSingle();

              if (!weeklyError && weeklyData) {
                weeklyAvailability = weeklyData;
              }
            }

            const result = {
              ...(settings || {}),
              timezone,
              bufferBefore: settings?.buffer_before || defaultSettings?.bufferBefore || 15,
              bufferAfter: settings?.buffer_after || defaultSettings?.bufferAfter || 15,
              minNoticeTime: settings?.min_notice_time || defaultSettings?.minNoticeTime || 60,
              userEnergySettings: profileData?.energy_profile || null,
              wakeTime: extractWakeTimeFromProfile(profileData),
              weeklyAvailability: weeklyAvailability
            };

            console.log('[useHostSettings] Final result:', result);

            // Cache the result
            settingsCache[cacheKey] = {
              data: result,
              timestamp: Date.now()
            };
            
            resolve(result);
          } catch (err) {
            console.error('[useHostSettings] Error fetching settings:', err);
            reject(new Error('Failed to fetch host settings'));
          } finally {
            // Clear the pending request after a small delay to handle race conditions
            setTimeout(() => {
              delete PENDING_REQUESTS[`settings_${cacheKey}`];
            }, 100);
          }
        });
        
        // Store the promise for deduplication
        PENDING_REQUESTS[`settings_${cacheKey}`] = requestPromise;
        return requestPromise;
      } catch (err) {
        console.error('[useHostSettings] Error initiating request:', err);
        throw new Error('Failed to fetch host settings');
      }
    },
    staleTime: STALE_TIME, // Consider data stale after this time
    gcTime: GC_TIME, // Garbage collection time (renamed from cacheTime)
    retry: 1,
    enabled: !profileLoading // Wait for profile data before fetching settings
  });

  const errorMessage = error || profileError ? (error || profileError as Error).message : null;
  const isLoadingCombined = loading || profileLoading;

  return {
    hostSettings: hostSettings || defaultSettings || {
      bufferBefore: 15,
      bufferAfter: 15,
      minNoticeTime: 60
    },
    loading: isLoadingCombined,
    error: errorMessage
  };
}

/**
 * Legacy fetchProfileData function - now uses shared hook internally
 * @deprecated Use useProfileData hook instead
 */
export async function fetchProfileData(userId: string): Promise<ProfileData | null> {
  console.warn('[fetchProfileData] This function is deprecated. Use useProfileData hook instead.');
  
  // For backward compatibility, we'll still support this but it will make a direct call
  // In practice, components should use the useProfileData hook
  const { supabase } = await import("@/integrations/supabase/client");
  
  try {
    console.log(`[fetchProfileData] Fetching profile for ${userId}`);
    const { data: fetchedProfile, error: profileError } = await supabase
      .from('profiles')
      .select('energy_profile')
      .eq('id', userId)
      .maybeSingle();
    
    if (profileError) {
      console.error('[fetchProfileData] Error fetching profile:', profileError);
      return null;
    }
    
    // Process the profile data to ensure energy_profile is properly typed
    const processedProfile: ProfileData | null = fetchedProfile ? {
      ...fetchedProfile,
      energy_profile: typeof fetchedProfile.energy_profile === 'object' 
        ? fetchedProfile.energy_profile 
        : typeof fetchedProfile.energy_profile === 'string'
          ? JSON.parse(fetchedProfile.energy_profile)
          : null
    } : null;
    
    return processedProfile;
  } catch (err) {
    console.error('[fetchProfileData] Error:', err);
    return null;
  }
}

// Re-export extractWakeTimeFromProfile for backward compatibility
export { extractWakeTimeFromProfile } from "@/hooks/shared/use-profile-data";
